import { html, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { EndpointModuleStatus, WidgetWithOverviewData } from '@nirvana/shared/dtos/types/widgets/common';
import { translateFn } from '../../../../utils/translator.helper';
import { isEmpty, isEqual } from 'lodash';
import { WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_MODULE_STATUS } from '../../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { ApexChartSeries, ApexOptions } from '../../../../charts/apexchart';
import ApexCharts from 'apexcharts';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';

/**
 * @summary Type here the description of this component
 * @tag endpoint-module-status
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_MODULE_STATUS)
export class EndpointModuleStatusWebComponent extends GzGenericWidgetContent {
  static get styles() {
    return styles;
  }

  @property({
    type: Array,
    attribute: true,
    reflect: true,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  payload: WidgetWithOverviewData;

  @query('#chartContainer')
  chartContainer: HTMLElement;

  private chart: ApexCharts;
  private rootStyles: CSSStyleDeclaration;
  private chartColors: string[] = [];
  private errorColor: string;
  private borderColor: string;
  private fontFamily: string;
  private fontSize: string;
  private graphLabelsBreakingPoint = 1000; // (in px) when widget width is smaller than this value, graph labels are not displayed

  private dataFields: Array<[string, string]> = [
    ['enabled', translateFn('Enabled')],
    ['disabled', translateFn('Disabled')],
    ['notInstalled', translateFn('Not installed')],
  ];

  changedInfoBoxText = `
          ${translateFn('You have <b>updates</b> for this widget!')}
          <br />
          <br />
          ${translateFn(
            'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
          )}`;

  protected renderContent(): TemplateResult {
    if (this.state === WidgetStateFrontEnd.IS_CHANGED) {
      return this.renderChangedInfoBox(this.changedInfoBoxText) as TemplateResult;
    }
    return html` <div class="chart-container" id="chartContainer"></div> `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setStyleVariables();
  }

  protected handleResize(): void {
    super.handleResize();
    this.adaptChartToParentHeight();
  }

  private adaptChartToParentHeight(): void {
    if (!this.chart || !this.parentElement) {
      return;
    }

    const verticalOffset = 72; // 24px + 16px padding, 32px title height
    const chartComputedHeight = this.parentElement?.offsetHeight - verticalOffset;
    const widgetWidth = this.parentElement?.offsetWidth;

    const xaxisOptions = {
      labels: {
        show: widgetWidth > this.graphLabelsBreakingPoint,
      },
    };

    this.chart.updateOptions({
      chart: {
        height: chartComputedHeight,
      },
      xaxis: xaxisOptions,
    });
  }

  private setStyleVariables(): void {
    this.rootStyles = getComputedStyle(document.body);
    //set error color to transparent in order to avoid rerendering the chart with error color
    //color variables is set in css file
    this.errorColor = 'transparent';
    this.chartColors = [
      this.rootStyles.getPropertyValue('--green-teal-chart-400'),
      this.rootStyles.getPropertyValue('--orange-alert-chart-100'),
      this.rootStyles.getPropertyValue('--teal-chart-050'),
    ];
    this.borderColor = this.rootStyles.getPropertyValue('--chart-101-pressed-dividers');
    this.fontFamily = this.rootStyles.getPropertyValue('--font-family');
    this.fontSize = this.rootStyles.getPropertyValue('--body-size');
  }

  async updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    await this.buildApexChart();
  }

  private getChartLabels(data: Array<EndpointModuleStatus>): string[][] {
    return data.map((endpointModuleStatus: EndpointModuleStatus) => endpointModuleStatus.name.split(' '));
  }

  private formatColumns(data: Array<EndpointModuleStatus>) {
    return this.dataFields.map(([key, value]) => {
      return {
        name: value,
        data: [...this.formatColumnByKey(data, key)],
      };
    });
  }

  private formatColumnByKey(data: Array<EndpointModuleStatus>, key: string): Array<any> {
    return data.map((element: EndpointModuleStatus) => {
      return element[key];
    });
  }

  private getFormatEndpointModulesNames(data: Array<EndpointModuleStatus>): Array<EndpointModuleStatus> {
    return data.map((element: EndpointModuleStatus) => {
      return {
        name: element.name,
        enabled: element.enabled,
        disabled: element.disabled,
        notInstalled: element.notInstalled,
      };
    });
  }

  private getBasicChartOptions(): ApexOptions {
    return {
      chart: {
        type: 'bar',
        height: 190,
        width: '100%',
        stacked: true,
        parentHeightOffset: 0,
        offsetY: 0,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 0,
          columnWidth: '60%',
          dataLabels: {
            total: {
              enabled: false,
            },
          },
        },
      },
      yaxis: {
        tickAmount: 4,
        floating: true,
        axisBorder: {
          show: false,
        },
        crosshairs: {
          show: true,
          position: 'front',
          stroke: {
            color: this.borderColor,
            width: 1,
          },
        },
        forceNiceScale: true,
        labels: {
          maxWidth: 50,
          offsetX: 16,
          style: {
            fontFamily: this.fontFamily,
          },
          formatter: (value: number) => value.toFixed(0),
        },
      },
      xaxis: {
        type: 'category',
        axisTicks: {
          show: false,
        },
        crosshairs: {
          show: true,
          position: 'front',
          stroke: {
            color: this.borderColor,
            width: 1,
          },
        },
        labels: {
          style: {
            fontFamily: this.fontFamily,
            cssClass: 'apexcharts-xaxis-label',
          },
          rotateAlways: true,
        },
        tooltip: {
          enabled: true,
          style: {
            fontFamily: this.fontFamily,
          },
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'left',
        offsetX: 0,
        fontFamily: this.fontFamily,
        markers: {
          width: 10,
          height: 10,
          strokeWidth: 0,
          radius: 0,
        },
        itemMargin: {
          horizontal: 0,
          vertical: 0,
        },
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      fill: {
        opacity: 1,
      },
      grid: {
        show: true,
        borderColor: this.borderColor,
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          left: 20,
        },
      },
    };
  }

  private computeYaxisTickAmount(dataSeries: EndpointModuleStatus[]): number {
    let highestValue = 0; // Initialize with the lowest possible value
    for (const dataPoint of dataSeries) {
      const maxPropertyValue = Math.max(dataPoint.enabled, dataPoint.disabled, dataPoint.notInstalled);
      if (maxPropertyValue > highestValue) {
        highestValue = maxPropertyValue;
      }
    }

    return highestValue <= 2 ? 1 : 4;
  }

  private getHighestDataValue(arr: EndpointModuleStatus[]): number {
    return arr.reduce((maxValue, obj) => {
      const numericValues = Object.values(obj).filter(value => typeof value === 'number') as Array<number>;

      const maxObjValue = Math.max(...numericValues);
      return Math.max(maxValue, maxObjValue);
    }, -Infinity);
  }

  private getActiveChartConfig(data: Array<EndpointModuleStatus>): ApexOptions {
    const formattedData: EndpointModuleStatus[] = this.getFormatEndpointModulesNames(data);
    const columns = this.formatColumns(formattedData);
    const labels: string[][] = this.getChartLabels(formattedData);
    const basicOptions: ApexOptions = this.getBasicChartOptions();
    const { xaxis, yaxis } = basicOptions;
    const tickAmount = this.computeYaxisTickAmount(formattedData);
    const minValue = this.getHighestDataValue(formattedData);
    const minWidthValue = minValue.toString().length >= 5 ? 25 : 15;
    return {
      ...basicOptions,
      series: columns,
      colors: this.chartColors,
      yaxis: {
        ...basicOptions.yaxis,
        tickAmount,
        labels: {
          ...yaxis?.labels,
          minWidth: minWidthValue,
        },
      },
      xaxis: {
        categories: labels,
        labels: {
          ...xaxis?.labels,
        },
        tooltip: {
          enabled: false,
        },
      },
      tooltip: {
        enabled: true,
        followCursor: false,
        shared: true,
        intersect: false,
        style: {
          fontSize: this.fontSize,
          fontFamily: this.fontFamily,
        },
      },
    };
  }

  private getEmptyChartConfig(): ApexOptions {
    const basicOptions: ApexOptions = this.getBasicChartOptions();
    const columns: ApexChartSeries[] = this.dataFields.map(([key, value]) => {
      return {
        name: value,
        data: key === 'notInstalled' ? new Array(14).fill(100) : new Array(14).fill(0),
      };
    });

    const { xaxis } = basicOptions;

    return {
      ...basicOptions,
      series: columns,
      colors: new Array(3).fill(this.errorColor),
      xaxis: {
        categories: new Array(14).fill('--'),
        labels: {
          ...xaxis?.labels,
          maxHeight: 100,
          maxWidth: 100,
          rotateAlways: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      tooltip: {
        enabled: false,
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
      responsive: [
        {
          breakpoint: 1366,
          options: {
            xaxis: {
              labels: {
                show: true,
              },
            },
          },
        },
      ],
    };
  }

  protected renderLoader(): TemplateResult {
    return html`<gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 8)"></gz-loader>
      <gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 8); width: 50%"></gz-loader>`;
  }

  private async buildApexChart(): Promise<void> {
    const endpointModulesStatusData: Array<EndpointModuleStatus> = this.payload?.endpointModulesStatus || [];

    const options: ApexOptions =
      this.state === WidgetStateFrontEnd.IS_ACTIVE && !isEmpty(endpointModulesStatusData)
        ? this.getActiveChartConfig(endpointModulesStatusData)
        : this.getEmptyChartConfig();

    if (!this.shadowRoot || !this.chartContainer) {
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new ApexCharts(this.chartContainer, options);
    await this.chart.render().then(() => this.adaptChartToParentHeight());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_MODULE_STATUS: EndpointModuleStatusWebComponent;
  }
}
